import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardMedia,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Warning as WarningIcon,
  AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import COLORS from '../../../Utils/colors';
import { request } from '../../../Utils/request';
import { getSignedUploadUrls, uploadFileToSignedUrl, getPublicUrlFromFileKey } from '../../../services/API/Api';
import { paiseToRupees } from '../../../Utils/currencyConverter';
import { useSelector } from 'react-redux';

interface Transaction {
  _id: string;
  reference: {
    tripId: string;
    paymentId: string;
  };
  type: 'credit' | 'debit';
  source: string;
  status: string;
  grossAmount: number;
  netAmount: number;
  note: string;
  createdAt: string;
}

interface BankDetails {
  accountType: string;
  bankName: string;
  ifscCode: string;
  phoneNumber: string;
  cancelledCheque?: File;
  cancelledChequeImage?: string; // URL for displaying existing image
}

interface WalletState {
  balance: number;
  hasBankDetails: boolean;
  transactions: Transaction[];
  isLoading: boolean;
}


const TransactionsTab: React.FC = () => {
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const [bankDetails, setBankDetails] = useState<BankDetails>({
    accountType: '',
    bankName: '',
    ifscCode: '',
    phoneNumber: '',
    cancelledChequeImage: '',
  });
  const [walletState, setWalletState] = useState<WalletState>({
    balance: 0,
    hasBankDetails: false,

    transactions: [],
    isLoading: false,
  });

  const [withdrawalAmount, setWithdrawalAmount] = useState<number>(1000);
  const [showWithdrawalDialog, setShowWithdrawalDialog] = useState(false);
  const [showBankDetailsDialog, setShowBankDetailsDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');

  const handleWithdrawFunds = () => {
    if (walletState.balance >= 1000) {
      setShowWithdrawalDialog(true);
    }
  };

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        setWalletState(prev => ({ ...prev, isLoading: true }));
        const response = await request('/profile/me/wallet', {
          method: 'GET',
          authorization: true,
        }) as any;

        // Update wallet state with API response
        if (response?.status) {
          setWalletState(prev => ({
            ...prev,
            balance: response.data.balance || prev.balance,
            hasBankDetails: response.data.hasBankDetails || false,
            transactions: response.data.transactions || [],
            isLoading: false,
          }));
        } else {
          // If no data received, just stop loading
          setWalletState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Error fetching wallet data:', error);
        setWalletState(prev => ({ ...prev, isLoading: false }));
      }
    };

    const fetchBankDetails = async () => {
      try {
        const response = await request('/profile/bank-information', {
          method: 'GET',
          authorization: true,
        }) as any;
        
        if(response?.status === true ) {
          console.log('response.data.bankDetail',response.data.bankInformation);
          const bankData = response.data.bankInformation;
          
          let cancelledChequeImageUrl = undefined;
          if (bankData.canceledCheque?.fileKey) {
            try {
              const publicUrlResponse = await getPublicUrlFromFileKey(bankData.canceledCheque.fileKey);
              cancelledChequeImageUrl = publicUrlResponse.data.files[0].viewUrl;
            } catch (error) {
              console.error('Error getting public URL for cancelled cheque:', error);
            }
          }
          
          setBankDetails({
            accountType: bankData.accountType || '',
            bankName: bankData.bankName || '',
            ifscCode: bankData.ifscCode || '',
            phoneNumber: bankData.phoneNumber || '',
            cancelledChequeImage: cancelledChequeImageUrl
          });
        }
      } catch (error) {
        console.error('Error fetching bank details:', error);
      }
    };

    if(isLoggedIn) {
      fetchWalletData();
      fetchBankDetails();
    }

  
  }, [])

  const confirmWithdrawal = async () => {
    try {
      // Validate withdrawal amount
      if (withdrawalAmount < 1000) {
        alert('Minimum withdrawal amount is ₹1,000');
        return;
      }
      
      // if (withdrawalAmount > walletState.balance) {
      //   alert('Withdrawal amount cannot exceed available balance');
      //   return;
      // }
      
      // if (!walletState.hasBankDetails) {
      //   alert('Please add bank details before withdrawing funds');
      //   return;
      // }
      
      setWalletState(prev => ({ ...prev, isLoading: true }));
      
      const response = await request('/profile/withdraw', {
        method: 'POST',
        authorization: true,
        body: {
          amount: withdrawalAmount,
        },
      }) as any;

      if (response?.status) {
        // Update balance and transactions from API response
        setWalletState(prev => ({
          ...prev,
          balance: response.data.balance || prev.balance,
          transactions: response.data.transactions || prev.transactions,
          isLoading: false,
        }));
        
        setShowWithdrawalDialog(false);
        setWithdrawalAmount(1000); // Reset to minimum amount
        
        // Show success message
        alert(`Withdrawal request for ₹${paiseToRupees(withdrawalAmount).toFixed(2)} submitted successfully!`);
        console.log('Withdrawal request submitted successfully');
      } else {
        setWalletState(prev => ({ ...prev, isLoading: false }));
        alert(`Withdrawal failed: ${response?.message || 'Unknown error'}`);
        console.error('Withdrawal failed:', response?.message);
      }
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      setWalletState(prev => ({ ...prev, isLoading: false }));
      alert('Error processing withdrawal request. Please try again.');
    }
  };

  const handleBankDetailsSubmit = async (bankDetails: BankDetails) => {
    try {
      setWalletState(prev => ({ ...prev, isLoading: true }));
      
      // Handle file upload for cancelled cheque
      let canceledChequeData = null;
      if (bankDetails.cancelledCheque) {
        try {
          // Step 1: Prepare file upload request payload
          const filesProp = [{
            folder: 'profile',
            fileType: bankDetails.cancelledCheque.type,
          }];
          
          const signedUrlsData = await getSignedUploadUrls(filesProp);
          const signedFiles = signedUrlsData?.data?.files || [];
          
          if (signedFiles.length > 0) {
            const { uploadUrl, fileKey } = signedFiles[0];
            
            // Step 2: Upload file to signed URL
            const uploadResponse = await uploadFileToSignedUrl(bankDetails.cancelledCheque, uploadUrl);
            
            if (uploadResponse.status === 200) {
              canceledChequeData = {
                fileKey,
                fileType: bankDetails.cancelledCheque.type,
                featured: true
              };
            }
          }
        } catch (fileError) {
          console.error('Error uploading cancelled cheque:', fileError);
          throw new Error('Failed to upload cancelled cheque file');
        }
      }
      
   

      const payload = {
        accountType: bankDetails.accountType.toLowerCase(),
        bankName: bankDetails.bankName,
        ifscCode: bankDetails.ifscCode,
        canceledCheque: canceledChequeData
      };

      const response = await request('/profile/bank-information', {
        method: 'PUT',
        authorization: true,
        body: payload,
      }) as any;

      if (response?.status) {
        setWalletState(prev => ({
          ...prev,
          hasBankDetails: true,
          isLoading: false,
        }));
        setShowBankDetailsDialog(false);
        console.log('Bank details submitted successfully');
      } else {
        console.error('Failed to submit bank details:', response?.message);
        setWalletState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      console.error('Error submitting bank details:', error);
      setWalletState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const canWithdraw = walletState.balance >= 1000;

  const formatCurrency = (amount: number) => `₹${paiseToRupees(amount).toFixed(2)}`;

  return (
    <Box sx={{}}>
      {/* Wallet Balance Section */}
      <Stack direction="column" spacing={2} alignItems="center" sx={{ mb: 2 }}>

        <Box
          // elevation={0}
          sx={{
            p: 3,
            mb: 2,
            background: 'linear-gradient(135deg, #C3E552 10%, #53C758 100%)',
            color: 'white',
            borderRadius: 2,
            width: '100%',
            mx: 6,
          }}
        >

          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2, fontSize: '2.5rem' }}>
            {formatCurrency(walletState.balance)}
          </Typography>

          {/* {!canWithdraw && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ 
              mb: 2,
              bgcolor: '#FF9800',
              color: '#263238',
              '& .MuiAlert-icon': { color: '#263238' },
              '& .MuiAlert-message': { color: '#263238' }
            }}
          >
            Withdrawal Not Available. Minimum ₹1,000 required to withdraw funds from your wallet.
          </Alert>
        )} */}


        </Box>
        {showWithdrawalDialog &&
          <Stack spacing={3} sx={{ width: '100%' }}>
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Amount to Withdraw
              </Typography>
              <TextField
                fullWidth
                label="Enter Amount"
                type="number"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(Number(e.target.value))}
              // inputProps={{ min: 1000, max: walletState.balance }}
              // InputProps={{
              //   startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              // }}
              // sx={{ '& .MuiInputBase-root': { fontSize: '1rem' } }}
              />
              {/* <Alert severity="success" sx={{ mt: 1, bgcolor: '#E8F5E8', color: '#2E7D32' }}>
                Funds will be transferred to the account ending with ***4321
                </Alert> */}
            </Box>
            <Button
              variant="contained"
              onClick={confirmWithdrawal}
              sx={{
                bgcolor: '#FF6B35',
                '&:hover': { bgcolor: '#E55A2B' }
              }}
            >
              Confirm Withdrawal
            </Button>
          </Stack>
        }

        <Button
          variant="outlined"
          size="medium"
          disabled={!canWithdraw}
          onClick={handleWithdrawFunds}
          sx={{
            borderRadius: '100px',
            borderColor: canWithdraw ? '#FF6B35' : '#E0E0E0',
            color: canWithdraw ? '#FF6B35' : '#E0E0E0',
            borderWidth: 1,
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            bgcolor: 'white',
            '&:hover': {
              borderColor: canWithdraw ? '#FF6B35' : '#E0E0E0',
              backgroundColor: 'white',
            },
          }}
        >
          Withdraw Funds
        </Button>
      </Stack>


      {/* Bank Details Section */}
      <Accordion
        defaultExpanded={!walletState.hasBankDetails}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: '1px solid #f0f0f0',
          '& .MuiAccordion-root': { boxShadow: 'none' },
          '& .MuiAccordionSummary-root': { px: 2 },
          '& .MuiAccordionDetails-root': { px: 2, pb: 2 }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: '56px',
            '& .MuiAccordionSummary-content': { my: 1 }
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {/* <AccountBalanceIcon sx={{ color: '#FF6B35', fontSize: '1.5rem' }} /> */}
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Bank detail
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box sx={{ gap: 2 }}>
              <TextField
                label="Account Type"
                value={bankDetails.accountType}
                fullWidth
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  accountType: e.target.value
                }))}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', mb: 2 } }}
              />
              <TextField
                label="Bank"
                value={bankDetails.bankName}
                fullWidth
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  bankName: e.target.value
                }))}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', mb: 2 } }}
              />
              <TextField
                label="IFSC Code"
                value={bankDetails.ifscCode}
                fullWidth
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  ifscCode: e.target.value
                }))}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', mb: 2 } }}
              />
              <TextField
                label="Phone number"
                value={bankDetails.phoneNumber}
                fullWidth
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  phoneNumber: e.target.value
                }))}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', mb: 2 } }}
              />
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom sx={{ fontSize: '0.9rem', mb: 2 }}>
                Cancelled cheque
              </Typography>
              
              {/* Show existing image from API */}
              {bankDetails.cancelledChequeImage && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Current cancelled cheque:
                  </Typography>
                  <CardMedia
                    component="img"
                    image={bankDetails.cancelledChequeImage}
                    alt="Cancelled cheque"
                    sx={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      border: '1px solid #E0E0E0',
                      borderRadius: 1,
                      backgroundColor: '#f5f5f5'
                    }}
                    onError={(e) => {
                      console.error('Failed to load image:', bankDetails.cancelledChequeImage);
                      // Hide the image if it fails to load
                      setBankDetails(prev => ({ ...prev, cancelledChequeImage: undefined }));
                    }}
                  />
                </Box>
              )}

              {/* Show image preview if new file is selected and it's an image */}
              {bankDetails.cancelledCheque && bankDetails.cancelledCheque.type.startsWith('image/') && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    New image selected:
                  </Typography>
                  <CardMedia
                    component="img"
                    image={URL.createObjectURL(bankDetails.cancelledCheque)}
                    alt="Cancelled cheque preview"
                    sx={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      border: '1px solid #E0E0E0',
                      borderRadius: 1,
                      backgroundColor: '#f5f5f5'
                    }}
                  />
                </Box>
              )}

              {/* Show file name for PDF files */}
              {bankDetails.cancelledCheque && bankDetails.cancelledCheque.type === 'application/pdf' && (
                <Box sx={{ mb: 2, p: 2, border: '1px solid #E0E0E0', borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="body2" color="text.secondary">
                    📄 {bankDetails.cancelledCheque.name}
                  </Typography>
                </Box>
              )}

              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
                fullWidth
                sx={{
                  borderColor: '#E0E0E0',
                  color: '#666',
                  '& .MuiButton-startIcon': { mr: 1 }
                }}
              >
                {bankDetails.cancelledCheque ? 'Change file' : 'Choose file'}
                <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  cancelledCheque: e.target.files?.[0]
                }))} />
              </Button>

              {/* Remove file button */}
              {bankDetails.cancelledCheque && (
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => setBankDetails(prev => ({
                    ...prev,
                    cancelledCheque: undefined
                  }))}
                  sx={{ mt: 1 }}
                >
                  Remove file
                </Button>
              )}
            </Box>
            <Button
              variant="contained"
              onClick={() => handleBankDetailsSubmit(bankDetails)}
              sx={{
                borderRadius:'100px',
                bgcolor: '#FF6B35',
                mt: 1,
                '&:hover': { bgcolor: '#E55A2B' }
              }}
            >
              Submit
            </Button>
          </Stack>
          {/* )} */}
        </AccordionDetails>
      </Accordion>

      {/* Transactions Section */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          borderRadius: 2,
          border: '1px solid #f0f0f0',
          '& .MuiAccordion-root': { boxShadow: 'none' },
          '& .MuiAccordionSummary-root': { px: 2 },
          '& .MuiAccordionDetails-root': { px: 2, pb: 2 }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: '56px',
            '& .MuiAccordionSummary-content': { my: 1 }
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {/* <CreditCardIcon sx={{ color: '#FF6B35', fontSize: '1.5rem' }} /> */}
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Transactions
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <List sx={{ p: 0 }}>
            {walletState.transactions.map((transaction, index) => (
              <React.Fragment key={transaction._id}>
                <Paper
                  elevation={2}
                  sx={{
                    mb: 1,
                    p: 2,
                    borderRadius: "16px",
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    {/* Left side - Icon based on transaction type */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: transaction.type === 'credit' ? '#4CAF50' : '#FF5252',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        flexShrink: 0
                      }}
                    >
                      {transaction.type === 'credit' ? (
                        <ArrowUpwardIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                      )}
                    </Box>

                    {/* Middle section - Transaction details */}
                    <Box sx={{ flexGrow: 1 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    <span style={{ color: '#1976D2' }}>
                      {(() => {
                        const tripId = transaction?.reference?.tripId || transaction._id;
                        return tripId.length > 6 ? `${tripId.slice(0, 6)}...` : tripId;
                      })()}
                    </span>
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
                        {(() => {
                          const paymentId = transaction?.reference?.paymentId || transaction._id;
                          return paymentId.length > 6 ? `${paymentId.slice(0, 6)}...` : paymentId;
                        })()}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
                          {new Date(transaction.createdAt).toLocaleDateString()}
                        </Typography>
                        {/* <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
                          {transaction.note}
                        </Typography> */}
                      </Stack>
                    </Box>

                    {/* Right side - Amount and status */}
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Stack spacing={0.5} alignItems="flex-end">
                        {transaction.type === 'debit' ?

                          <Typography variant="h6" sx={{ color: '#000', fontSize: '1.1rem', fontWeight: 600 }}>
                            {formatCurrency(transaction.netAmount)}
                          </Typography>
                          :
                          <Typography variant="h6" sx={{ color: '#000', fontSize: '1.1rem', fontWeight: 600 }}>
                            {formatCurrency(transaction.grossAmount)}
                          </Typography>

                        }
                        {/* <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
                          Gross: {formatCurrency(transaction.grossAmount)}
                        </Typography> */}
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
                          {transaction.source}
                        </Typography>
                        {/* <Typography variant="body2" sx={{
                          fontSize: '0.8rem',
                          color: transaction.status === 'available' ? '#4CAF50' : '#FF9800',
                          fontWeight: 500
                        }}>
                          {transaction.status}
                        </Typography> */}
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </React.Fragment>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>


      {/* Bank Details Dialog */}
      <Dialog
        open={showBankDetailsDialog}
        onClose={() => setShowBankDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <AccountBalanceIcon sx={{ color: '#FF6B35' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Update Bank Details</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Account Type"
                value={bankDetails.accountType}
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  accountType: e.target.value
                }))}
                fullWidth
                size="small"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem' } }}
              />
              <TextField
                label="Bank"
                value={bankDetails.bankName}
                fullWidth
                size="small"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem' } }}
              />
              <TextField
                label="IFSC Code"
                value={bankDetails.ifscCode}
                fullWidth
                size="small"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem' } }}
              />
              <TextField
                label="Phone no"
                value={bankDetails.phoneNumber}
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  phoneNumber: e.target.value
                }))}
                fullWidth
                size="small"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem' } }}
              />
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom sx={{ fontSize: '0.9rem' }}>
                Cancelled cheque
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
                fullWidth
                sx={{
                  borderColor: '#E0E0E0',
                  color: '#666',
                  '& .MuiButton-startIcon': { mr: 1 }
                }}
              >
                Choose file
                <input type="file" hidden accept="image/*,.pdf" />
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowBankDetailsDialog(false)}
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleBankDetailsSubmit(bankDetails)}
            sx={{
              bgcolor: '#FF6B35',
              px: 3,
              '&:hover': { bgcolor: '#E55A2B' }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionsTab;
